// S2-CC6: LLM Control Center — matches wireframe v3
// S2-CC16: Platform cost hero row added (sticky band beneath emergency bar)
// S2-CC17: Section 3 rewritten — TabSwitcher pivot + DonutChart + token distribution Histogram
// S2-CC20.6b.6: Section 5 (Recent Calls) replaced by <CallLogView />; new Sections 8+9 appended
import { useEffect, useMemo, useRef, useState } from 'react';
import { apiPost } from '../utils/api';
import { useLLMStatus } from '../hooks/useLLMStatus';
import { useLLMReport } from '../hooks/useLLMReport';
import { useJobs } from '../hooks/useJobs';
import { MetricCard } from '../components/MetricCard';
import { ProgressBar } from '../components/ProgressBar';
import { TabSwitcher } from '../components/TabSwitcher';
import { DonutChart } from '../components/charts/DonutChart';
import { Histogram } from '../components/charts/Histogram';
import { EmptyState } from '../components/EmptyState';
// S2-CC20.6b.6: new self-contained view compositions (own Card, hooks, state).
import { CallLogView } from '../components/llm/CallLogView';
import { BudgetTreeView } from '../components/llm/BudgetTreeView';
import { ReconciliationView } from '../components/llm/ReconciliationView';
import './LLMControl.css';

// ── S2-CC17 helpers ─────────────────────────────────────────────
// Humanize the raw backend keys into chart legend labels.
// Models come in as 'claude-sonnet-4-6' / 'claude-haiku-4-5-20251001' /
// 'claude-opus-4-6' — we surface just the family. Eval types come in as
// lowercase tokens ('bare', 'full', 'readiness', ...); map the common ones
// to friendlier strings and title-case anything unknown.
function formatKey(key: string, pivot: 'model' | 'eval'): string {
  if (pivot === 'model') {
    if (key.includes('haiku')) return 'Haiku';
    if (key.includes('opus')) return 'Opus';
    if (key.includes('sonnet')) return 'Sonnet';
    return key;
  }
  // pivot === 'eval'
  if (key === 'unknown') return 'Unknown';
  if (key === 'bare') return 'Bare eval';
  if (key === 'full') return 'Full-skill eval';
  if (key === 'readiness') return 'Readiness';
  if (key === 'production') return 'Production';
  if (key === 'smoke') return 'Smoke test';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

// Compact token count formatter — mirrors the wireframe's "14.2k" / "156k" style.
// Rationale: MetricCard values are display numbers, so we strip digits once they
// stop carrying information (<10k keeps one decimal; >=10k rounds to whole k).
function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10000) return (n / 1000).toFixed(1) + 'k';
  return Math.round(n / 1000) + 'k';
}

const TIPS: Record<string, [string, string]> = {
  gateway: ['Gateway', 'Single checkpoint ALL Claude API calls pass through. When paused, zero calls reach Anthropic. Your instant kill switch.'],
  worker: ['Worker', 'Background process that picks up evaluation jobs. Pausing means running jobs finish but no new ones start.'],
  apiKey: ['API Key', 'Anthropic API key authenticates calls to Claude. When disabled, every call fails immediately.'],
  inputTokens: ['Input Tokens', 'Tokens sent TO Claude — prompt, tools, schema, product data. $3/MTok for Sonnet. Typically 5x more volume than output.'],
  outputTokens: ['Output Tokens', 'Tokens generated BY Claude — listing content, scores, reasoning. $15/MTok for Sonnet — 5x the input price.'],
  dailyLimit: ['Daily Limit', 'Maximum spend per day. Gateway blocks all calls when hit. Resets at midnight UTC.'],
  jobLimit: ['Per-Job Limit', 'Maximum spend for a single evaluation job. Prevents runaway costs.'],
  variance: ['Variance', 'Difference between estimated and actual. Negative = under estimate (saved). Positive = over estimate.'],
  pareto: ['Cost Distribution', '80/20 rule — which operations consume the most budget. Optimize the biggest bars for highest ROI.'],
  spend: ['Daily Spend', 'Real-time LLM API costs. Tracks actual tokens from every Anthropic response.'],
  jobs: ['Jobs', 'Every background evaluation job. Click a row to see details.'],
  // `calls` TIP removed in S2-CC20.6b.6 — the new <CallLogView /> supplies its own
  // header copy and does not depend on the parent TIPS registry.
};

// S2-CC18 helpers (modelBadge, costColorClass, evalBadge) removed in S2-CC20.6b.6 —
// they were the in-memory Section 5 rendering layer. The new <CallLogView /> owns
// its own status pill, model label, and cost color classification internally.

export default function LLMControl() {
  const { status, refresh: refreshStatus } = useLLMStatus();
  const { report, refresh: refreshReport } = useLLMReport();
  const { jobs, cancelJob, deleteJob, purgeJobs, refresh: refreshJobs } = useJobs();
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [tip, setTip] = useState<{ key: string; x: number; y: number } | null>(null);

  const sysState = !status ? 'disabled' : status.api_key_status !== 'active' ? 'disabled' : status.paused ? 'paused' : 'active';
  const refreshAll = () => { refreshStatus(); refreshReport(); refreshJobs(); };

  const handleStopAll = async () => {
    await Promise.all([apiPost('/v1/llm/pause', {}), apiPost('/v1/eval/jobs/cancel-all', {}), apiPost('/v1/eval/worker/pause', {})]);
    refreshAll();
  };
  const handlePauseWorker = async () => { await apiPost('/v1/eval/worker/pause', {}); refreshAll(); };
  const handleToggleGateway = async () => { await apiPost(status?.paused ? '/v1/llm/resume' : '/v1/llm/pause', {}); refreshAll(); };

  const I = ({ k }: { k: string }) => (
    <i className="info-i" onClick={e => { e.stopPropagation(); setTip(tip?.key === k ? null : { key: k, x: e.clientX, y: e.clientY }); }}>i</i>
  );

  const dailyCost = status?.daily_cost_cents ?? 0;
  const dailyLimit = report?.summary.daily_limit_cents ?? 500;
  const pctUsed = dailyLimit > 0 ? (dailyCost / dailyLimit * 100) : 0;
  const totalInput = report ? Object.values(report.by_model).reduce((s, m) => s + m.input_tokens, 0) : 0;
  const totalOutput = report ? Object.values(report.by_model).reduce((s, m) => s + m.output_tokens, 0) : 0;

  // Per-million pricing — mirrors services/intelligence/src/llm/gateway.ts PRICING constant.
  // Used to split daily cost into input vs output so the stacked bars can show the true driver.
  const PRICING: Record<string, { input: number; output: number; cache: number }> = {
    'claude-sonnet-4-6':          { input: 3 / 1_000_000,    output: 15 / 1_000_000, cache: 0.30 / 1_000_000 },
    'claude-haiku-4-5-20251001':  { input: 0.80 / 1_000_000, output: 4 / 1_000_000,  cache: 0.08 / 1_000_000 },
    'claude-opus-4-6':            { input: 15 / 1_000_000,   output: 75 / 1_000_000, cache: 1.50 / 1_000_000 },
  };
  const priceFor = (m: string) => PRICING[m] ?? PRICING['claude-sonnet-4-6'];

  // Input/output cost split (cents). Sum across every recorded call; fallback to token-ratio if no calls yet.
  const inputCost = report?.calls.reduce((s, c) => s + c.input_tokens * priceFor(c.model).input * 100, 0) ?? 0;
  const outputCost = report?.calls.reduce((s, c) => s + c.output_tokens * priceFor(c.model).output * 100, 0) ?? 0;
  const totalCost = inputCost + outputCost;
  const inputCostPct = totalCost > 0 ? (inputCost / totalCost * 100) : 0;
  const outputCostPct = totalCost > 0 ? (outputCost / totalCost * 100) : 0;
  const totalTokens = totalInput + totalOutput;

  // Agent → Tool aggregation: group calls by (caller, model) for the right-hand table.
  // Caller strings from gateway.ts look like "session-handler.ts:create_listing" or "evaluate.ts:runWithMessagesAPI".
  const agentToolMap = new Map<string, { caller: string; model: string; calls: number; cost: number }>();
  report?.calls.forEach(c => {
    const key = `${c.caller}|${c.model}`;
    const prev = agentToolMap.get(key) ?? { caller: c.caller, model: c.model, calls: 0, cost: 0 };
    prev.calls += 1;
    prev.cost += c.cost_cents;
    agentToolMap.set(key, prev);
  });
  const agentToolRows = [...agentToolMap.values()].sort((a, b) => b.cost - a.cost);

  // Parse a caller string like "session-handler.ts:create_listing" into display pieces.
  // Previously mapped scripts to agent names via a hardcoded agentMap (CW-specific leak).
  // Now returns the raw caller and tool — agent identity lives in the agent manifest
  // (AC-9 role_label). This function becomes richer when AC-2 (operation_type auto-read) lands.
  const parseCaller = (caller: string): { agent: string; tool: string } => {
    const [left, right] = caller.split(':');
    const raw = (left ?? caller).replace(/\.ts$/, '');
    return { agent: raw, tool: right ?? '—' };
  };

  // Map full model id to the short chip label + class used by .td-model.{sonnet,haiku,opus,local}.
  const modelChip = (model: string): { label: string; cls: string } => {
    if (model.includes('haiku')) return { label: 'Haiku', cls: 'haiku' };
    if (model.includes('opus'))  return { label: 'Opus',  cls: 'opus' };
    if (model.includes('sonnet'))return { label: 'Sonnet',cls: 'sonnet' };
    return { label: model || 'local', cls: 'local' };
  };

  // ── S2-CC16: Hero row — Projected EOD pace calculation ──────────────
  // Approach: "pace" = cents spent in the last rolling 60 minutes across today's calls.
  // Fallback to average hourly spend so far when no call has landed in the last hour.
  // Rationale: rolling window is responsive to recent bursts; fallback prevents
  // projected == dailyCost when there's sparse traffic.
  const dailyCalls = status?.daily_calls ?? 0;
  const showProjection = dailyCalls >= 3;  // Sparse threshold per wireframe

  // Compute pace (cents/hour) only when we have enough signal; otherwise 0.
  let projected = dailyCost;
  if (showProjection && report?.calls && report.calls.length > 0) {
    const now = Date.now();
    const oneHourAgo = now - 3_600_000;
    // Last-hour spend: hash-bucket today's calls into the rolling 60min window.
    const lastHourCost = report.calls
      .filter(c => new Date(c.timestamp).getTime() >= oneHourAgo)
      .reduce((sum, c) => sum + c.cost_cents, 0);

    // Fractional hour: current local hour + minutes/60 so pace tracks sub-hour accuracy.
    const d = new Date();
    const hoursElapsed = Math.max(0.1, d.getHours() + d.getMinutes() / 60);
    const hoursRemaining = Math.max(0.1, 24 - hoursElapsed);

    // pace: prefer last-hour data; fall back to average-so-far if the last hour is empty.
    const pace = lastHourCost > 0 ? lastHourCost : dailyCost / hoursElapsed;
    projected = dailyCost + pace * hoursRemaining;
  }

  // Observability: one-shot mount log + sparse-state log.
  // Runs once per component mount; guarded by [] dep list, not every render.
  useEffect(() => {
    console.log('[llm-control] hero mounted', {
      dailyCost,
      dailyCalls,
      dailyLimit,
      pctUsed,
      sparse: dailyCalls < 3,
    });
    if (dailyCalls < 3) {
      console.log('[llm-control] hero sparse state', { dailyCalls });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── S2-CC17: Section 3 state + derivations ──────────────────────
  // Pivot toggle drives whether the donut groups by model or eval type.
  const [pivot, setPivot] = useState<'model' | 'eval'>('model');

  // Build the donut input from the active pivot source. Sorted descending by
  // cost so the biggest slice renders at 12 o'clock (matches the wireframe).
  // Memoized — Object.entries + sort shouldn't rerun unless report or pivot changes.
  const donutSegments = useMemo(() => {
    if (!report) return [];
    const source = pivot === 'model' ? report.by_model : report.by_eval_type;
    if (!source) return [];
    return Object.entries(source)
      .map(([key, data]) => ({
        id: key,
        label: formatKey(key, pivot),
        value: data.cost_cents,
      }))
      .sort((a, b) => b.value - a.value);
  }, [report, pivot]);

  // Token distribution stats for the right panel. Fall back to all-zeros when
  // the backend field is absent (old response cache, backend rollback, etc.)
  // so the panel renders structure rather than crashing.
  const tokenDist = report?.token_distribution ?? {
    avg: 0,
    median: 0,
    p95: 0,
    max: 0,
    buckets: [],
  };

  // One-shot empty-state log. Ref-guarded so it fires exactly once per session
  // (the first render where dailyCalls < 5) rather than on every re-render.
  const section3EmptyLoggedRef = useRef(false);
  useEffect(() => {
    if (dailyCalls < 5 && !section3EmptyLoggedRef.current) {
      section3EmptyLoggedRef.current = true;
      console.log('[llm-control] section3 empty state', { dailyCalls });
    }
  }, [dailyCalls]);

  return (
    <div className="llm-page" onClick={() => setTip(null)}>
      {tip && TIPS[tip.key] && (
        <div className="tip-card" style={{ left: Math.min(tip.x, window.innerWidth - 340), top: tip.y + 20 }} onClick={e => e.stopPropagation()}>
          <div className="tip-title">{TIPS[tip.key][0]}</div>{TIPS[tip.key][1]}
        </div>
      )}

      {/* ═══ SECTION 1: Emergency Bar ═══ */}
      <header className="emergency-bar">
        <div className={`sys-status ${sysState}`}>
          <div className="sys-dot" /><span>System {sysState === 'active' ? 'Active' : sysState === 'paused' ? 'Paused' : 'Disabled'}</span>
        </div>
        <div className="em-divider" />
        <button className="em-btn em-btn-stop" onClick={handleStopAll}>
          <span className="em-btn-title">STOP ALL <I k="gateway" /></span>
          <span className="em-btn-sub">Stops everything. Zero cost. Instant.</span>
        </button>
        <button className="em-btn em-btn-pause" onClick={handlePauseWorker}>
          <span className="em-btn-title">Pause New Jobs <I k="worker" /></span>
          <span className="em-btn-sub">Running jobs finish. No new ones start.</span>
        </button>
        <button className="em-btn em-btn-block" onClick={handleToggleGateway}>
          <span className="em-btn-title">{status?.paused ? 'Resume Gateway' : 'Block API'} <I k="apiKey" /></span>
          <span className="em-btn-sub">{status?.paused ? 'Allow Claude calls again.' : 'All Claude calls fail immediately.'}</span>
        </button>
        <div className="em-divider" />
        <div className="em-spacer" />
        <div className="em-meta">
          <div>Today: <strong>{dailyCost.toFixed(1)}¢</strong> / {dailyLimit}¢</div>
          <div style={{ marginTop: 2, fontSize: 11 }}>{status?.daily_calls ?? 0} calls · {totalInput.toLocaleString()} tokens</div>
        </div>
      </header>

      {/* ═══ S2-CC16: Platform cost hero row ═══ */}
      {/* Sticky band between emergency bar and main content. Shows headline KPIs:
          total spent, projected EOD (with sparse-state guard), and call count. */}
      <section className="hero-row">
        <div className="hero-grid">
          {/* Column 1 — Total spend today (display KPI) */}
          <MetricCard
            size="hero"
            mono
            status="neutral"
            value={`${dailyCost.toFixed(1)}¢`}
            label="Spent today"
          />

          {/* Vertical divider between col 1 and col 2 */}
          <div className="hero-vert-div" />

          {/* Column 2 — Projected EOD, or sparse-state copy if <3 calls */}
          {showProjection ? (
            <MetricCard
              size="md"
              mono
              status="neutral"
              value={`${projected.toFixed(1)}¢`}
              label="Projected EOD"
            />
          ) : (
            <div className="hero-sparse">Need 3+ calls for forecast</div>
          )}

          {/* Vertical divider between col 2 and col 3 */}
          <div className="hero-vert-div" />

          {/* Column 3 — Total calls today (meaningful even when sparse) */}
          <MetricCard
            size="md"
            mono
            status="neutral"
            value={`${dailyCalls}`}
            label="Total calls"
          />
        </div>

        {/* Usage bar row — full width under the 3 columns */}
        <div className="hero-usage-row">
          <div className="hero-usage-bar-wrap">
            <ProgressBar value={Math.min(100, pctUsed)} variant="primary" size="md" />
          </div>
          <span className="hero-usage-caption">
            {dailyCost.toFixed(1)}¢ of {dailyLimit}¢ daily limit ({pctUsed.toFixed(1)}%)
          </span>
        </div>
      </section>

      <div className="llm-content">
        {/* ═══ SECTION 2: Daily Spend ═══ */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Daily Spend <I k="spend" /></span>
            <span className="card-sub">Today · Resets midnight IST</span>
          </div>

          <div className="spend-grid">

            {/* Column 1 — half-circle gauge */}
            <div className="gauge-wrap">
              <div className="gauge-arc">
                <svg viewBox="0 0 140 140" width="140" height="140">
                  <path d="M 20 110 A 50 50 0 1 1 120 110" stroke="var(--surface-2)" strokeWidth="10" fill="none" strokeLinecap="round" />
                  {/* Arc length ≈ 157 (half-circle radius 50). Fill proportional to pctUsed. */}
                  <path d="M 20 110 A 50 50 0 1 1 120 110"
                    stroke="var(--primary-500)" strokeWidth="10" fill="none" strokeLinecap="round"
                    strokeDasharray="157"
                    strokeDashoffset={157 - (157 * Math.min(100, pctUsed) / 100)} />
                </svg>
              </div>
              <div className="gauge-label">
                <div className="gauge-total">{dailyCost.toFixed(1)}¢</div>
                <div className="gauge-limit">of {dailyLimit}¢ daily limit ({pctUsed.toFixed(1)}%)</div>
              </div>
            </div>

            {/* Column 2 — total / input / output stacked bars + alert */}
            <div className="token-breakdown">
              <div className="tok-row">
                <div className="tok-header">
                  <span className="tok-name">Total Spend</span>
                  <span className="tok-cost total">{dailyCost.toFixed(1)}¢</span>
                </div>
                <div className="tok-bar-track">
                  <div className="tok-bar-fill total" style={{ width: `${Math.min(100, pctUsed)}%` }} />
                </div>
                <div className="tok-meta">{totalTokens.toLocaleString()} tokens · {pctUsed.toFixed(1)}% of daily limit</div>
              </div>

              <div className="tok-divider" />

              <div className="tok-row">
                <div className="tok-header">
                  <span className="tok-name">Input Tokens <I k="inputTokens" /></span>
                  <span className="tok-cost input">{inputCost.toFixed(1)}¢ <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>({inputCostPct.toFixed(0)}%)</span></span>
                </div>
                <div className="tok-bar-track">
                  <div className="tok-bar-fill input" style={{ width: `${inputCostPct}%` }} />
                </div>
                <div className="tok-meta">{totalInput.toLocaleString()} tokens — system prompts, schemas, product data</div>
              </div>

              <div className="tok-row">
                <div className="tok-header">
                  <span className="tok-name">Output Tokens <I k="outputTokens" /></span>
                  <span className="tok-cost output">{outputCost.toFixed(1)}¢ <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>({outputCostPct.toFixed(0)}%)</span></span>
                </div>
                <div className="tok-bar-track">
                  <div className="tok-bar-fill output" style={{ width: `${outputCostPct}%` }} />
                </div>
                <div className="tok-meta">{totalOutput.toLocaleString()} tokens — generated listings, quality scores</div>
              </div>

              {/* Driver alert: only surface when there's enough data and input dominates */}
              {totalCost > 0 && inputCostPct >= 60 && (
                <div className="tok-alert">
                  Input is the cost driver ({inputCostPct.toFixed(0)}%). Reducing prompt size has the highest ROI.
                </div>
              )}
            </div>

            {/* Column 3 — Agent → Tool breakdown table */}
            <div>
              <div className="agent-tool-label">By Agent &amp; Tool</div>
              <table className="agent-tool-table">
                <thead>
                  <tr>
                    <th>Agent → Tool</th>
                    <th>Calls</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {agentToolRows.length === 0 ? (
                    <tr><td colSpan={3} style={{ color: 'var(--text-muted)', fontSize: 12, padding: '12px 0' }}>No calls yet today</td></tr>
                  ) : agentToolRows.map((row, i) => {
                    const { agent, tool } = parseCaller(row.caller);
                    const chip = modelChip(row.model);
                    return (
                      <tr key={i}>
                        <td>
                          <div className="td-name">{agent}</div>
                          <div className="td-tool">{tool} <span className={`td-model ${chip.cls}`}>{chip.label}</span></div>
                        </td>
                        <td className="td-calls">{row.calls}</td>
                        <td className={`td-cost ${row.cost === 0 ? 'zero' : ''}`}>{row.cost.toFixed(1)}¢</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

          </div>
        </div>

        {/* ═══ SECTION 3: Cost Distribution (S2-CC17) ═══ */}
        {/* Two-panel layout: left = donut grouped by pivot (model / eval type),
            right = token-distribution stats + histogram. Below a 5-call signal
            threshold we suppress both to avoid misleading tiny charts. */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Cost Distribution <I k="pareto" /></span>
            <TabSwitcher
              tabs={[
                { id: 'model', label: 'Model' },
                { id: 'eval', label: 'Eval Type' },
              ]}
              activeId={pivot}
              onChange={(id) => {
                // Cast is safe — tabs list is the only source of ids passed in.
                setPivot(id as 'model' | 'eval');
                console.log('[llm-control] section3 pivot changed', { to: id });
              }}
              size="sm"
              ariaLabel="Cost distribution pivot"
            />
          </div>

          {dailyCalls < 5 ? (
            /* Sparse-state guard: signal below 5 calls is too noisy to plot. */
            <EmptyState
              title="Need ≥5 calls for distribution signal"
              detail={`${dailyCalls} / 5 calls today`}
            />
          ) : (
            <div className="dist-grid">
              {/* LEFT — cost-by-pivot donut. Legend carries the per-slice pct + cost. */}
              <div className="dist-panel">
                <div className="dist-panel-label">
                  By {pivot === 'model' ? 'Model' : 'Eval Type'}
                </div>
                <DonutChart
                  segments={donutSegments}
                  centerValue={`${dailyCost.toFixed(1)}¢`}
                  centerLabel="today"
                  size={180}
                  ringThickness={22}
                  showLegend={true}
                />
              </div>

              {/* RIGHT — token distribution: summary stats (avg/median/p95)
                  plus a histogram of call counts bucketed by total tokens. */}
              <div className="dist-panel">
                <div className="dist-panel-label">Token Distribution</div>
                <div className="dist-stat-grid">
                  <MetricCard
                    size="md"
                    mono
                    status="neutral"
                    value={formatTokens(tokenDist.avg)}
                    label="Avg tokens / call"
                  />
                  <MetricCard
                    size="md"
                    mono
                    status="neutral"
                    value={formatTokens(tokenDist.median)}
                    label="Median"
                  />
                  <MetricCard
                    size="md"
                    mono
                    status="neutral"
                    value={formatTokens(tokenDist.p95)}
                    label="P95"
                  />
                </div>
                <div className="dist-stat-divider" />
                <Histogram
                  buckets={tokenDist.buckets}
                  caption={`peak: ${formatTokens(tokenDist.max)} tokens`}
                />
              </div>
            </div>
          )}
        </div>

        {/* ═══ SECTION 4: Jobs ═══ */}
        <div className="card">
          <div className="jobs-header">
            <span className="card-title">Jobs <I k="jobs" /></span>
            <button className="job-btn-purge" onClick={purgeJobs}>Purge Done</button>
          </div>
          <table className="jobs-table">
            <thead><tr><th>Status</th><th>Agent</th><th>Type</th><th>Duration</th><th>Cost</th><th>Action</th></tr></thead>
            <tbody>
              {jobs.map(job => {
                const cost = report?.by_job[job.job_id] ?? 0;
                const dur = job.completed_at && job.started_at ? `${Math.round((new Date(job.completed_at).getTime() - new Date(job.started_at).getTime()) / 1000)}s` : job.status === 'running' ? '...' : '—';
                return (
                  <><tr key={job.job_id} className="clickable" onClick={() => setExpandedJob(expandedJob === job.job_id ? null : job.job_id)}>
                    <td><span className={`job-badge ${job.status}`}>{job.status}</span></td>
                    <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{job.agent_id}</td>
                    <td>{job.type}</td>
                    <td style={{ fontFamily: 'var(--font-mono)' }}>{dur}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', color: cost > 0 ? 'var(--success-dark-text)' : 'var(--text-muted)' }}>{cost > 0 ? `${cost.toFixed(1)}¢` : '—'}</td>
                    <td onClick={e => e.stopPropagation()}>
                      {(job.status === 'running' || job.status === 'pending') && <button className="job-btn job-btn-cancel" onClick={() => cancelJob(job.job_id)}>Cancel</button>}
                      {(job.status === 'complete' || job.status === 'failed') && <button className="job-btn job-btn-delete" onClick={() => deleteJob(job.job_id)}>Delete</button>}
                    </td>
                  </tr>
                  {expandedJob === job.job_id && (
                    <tr key={`${job.job_id}-x`}><td colSpan={6} style={{ padding: 0 }}>
                      <div className="job-expand">
                        <div className="job-expand-label">Details</div>
                        <div className="job-expand-text">
                          ID: <span style={{ fontFamily: 'var(--font-mono)' }}>{job.job_id}</span><br />
                          Config: <span style={{ fontFamily: 'var(--font-mono)' }}>{String((job.params as Record<string, unknown>)?.config_id ?? '—')}</span><br />
                          Products: <span style={{ fontFamily: 'var(--font-mono)' }}>{JSON.stringify((job.params as Record<string, unknown>)?.products ?? [])}</span>
                        </div>
                        {job.error && <><div className="job-expand-label">Error</div><div className="job-expand-text" style={{ color: 'var(--error-dark-text)' }}>{job.error}</div></>}
                      </div>
                    </td></tr>
                  )}</>
                );
              })}
              {jobs.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No jobs</td></tr>}
            </tbody>
          </table>
        </div>

        {/* ═══ SECTION 6: Call Log (S2-CC20.6b.4) ═══ */}
        {/* Replaces the previous in-memory Recent Calls table. <CallLogView />
            owns its own Card wrapper, filter chips, table, pagination, and
            empty/error states. It reads from /v1/llm/calls via useLLMCalls(). */}
        <CallLogView />

        {/* ═══ SECTION 7: Config ═══ */}
        <div style={{ marginBottom: 20 }}>
          <div className="section-label">Configuration</div>
          <div className="config-grid">
            <div className="config-tile"><div className="config-value">{dailyLimit}¢</div><div className="config-label">Daily Limit <I k="dailyLimit" /></div><div className="config-env">LLM_DAILY_LIMIT_CENTS</div></div>
            <div className="config-tile"><div className="config-value">200¢</div><div className="config-label">Per-Job Limit <I k="jobLimit" /></div><div className="config-env">LLM_JOB_LIMIT_CENTS</div></div>
            <div className="config-tile"><div className="config-value" style={{ color: status?.api_key_status === 'active' ? 'var(--success-dark-text)' : 'var(--error-dark-text)' }}>{status?.api_key_status === 'active' ? 'Active' : status?.api_key_status === 'disabled' ? 'Disabled' : 'Missing'}</div><div className="config-label">API Key <I k="apiKey" /></div></div>
            <div className="config-tile"><div className="config-value" style={{ color: status?.paused ? 'var(--warning-dark-text)' : 'var(--success-dark-text)' }}>{status?.paused ? 'Paused' : 'Active'}</div><div className="config-label">Gateway <I k="gateway" /></div></div>
          </div>
        </div>

        {/* ═══ SECTION 8: Budget Tree (S2-CC20.6b.3) ═══ */}
        {/* New — hierarchical budget tree read from /v1/llm/budgets. Self-contained. */}
        <BudgetTreeView />

        {/* ═══ SECTION 9: Reconciliation (S2-CC20.6b.5) ═══ */}
        {/* New — ledger-vs-provider reconciliation read from /v1/llm/reconciliation.
            Self-contained; today shows the `not_configured` stub state. */}
        <ReconciliationView />
      </div>
    </div>
  );
}
